import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/googleMaps";
import { Loader2, MapPinOff, LocateFixed } from "lucide-react";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
// Default center: Muscat, Oman
const DEFAULT = { lat: 23.588, lng: 58.3829 };

export default function MapPicker({ value, onChange, height = "h-[380px]" }) {
  const mapRef = useRef(null);
  const acRef = useRef(null); // container for the modern PlaceAutocompleteElement
  const acElRef = useRef(null);
  const markerRef = useRef(null);
  const gmapRef = useRef(null);
  const gmapsRef = useRef(null);
  const lastEmitRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    if (!KEY) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    loadGoogleMaps(KEY)
      .then((maps) => {
        if (cancelled || !mapRef.current) return;

        const hasVal =
          value?.latitude !== "" && value?.latitude != null && value?.longitude !== "" && value?.longitude != null;
        const start = hasVal
          ? { lat: parseFloat(value.latitude), lng: parseFloat(value.longitude) }
          : DEFAULT;

        const map = new maps.Map(mapRef.current, {
          center: start,
          zoom: hasVal ? 16 : 12,
          streetViewControl: false,
          fullscreenControl: true,
          clickableIcons: false,
          gestureHandling: "greedy",
          // Enable Map / Satellite (with labels) toggle
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: maps.ControlPosition.TOP_LEFT,
            mapTypeIds: ["roadmap", "hybrid"],
          },
        });
        const geocoder = new maps.Geocoder();
        const marker = new maps.Marker({ position: start, map, draggable: true });
        gmapRef.current = map;
        gmapsRef.current = maps;
        markerRef.current = marker;

        // Ensure the map paints correctly once its container has final dimensions.
        setTimeout(() => {
          maps.event.trigger(map, "resize");
          map.setCenter(start);
        }, 350);

        const emit = (pos, address) => {
          const lat = +pos.lat.toFixed(6);
          const lng = +pos.lng.toFixed(6);
          lastEmitRef.current = { lat, lng };
          onChange({ latitude: lat, longitude: lng, address });
        };

        const reverse = (pos) => {
          geocoder.geocode({ location: pos }, (results, gstatus) => {
            const address = gstatus === "OK" && results?.[0] ? results[0].formatted_address : null;
            emit(pos, address);
          });
        };

        marker.addListener("dragend", (e) => reverse({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
        map.addListener("click", (e) => {
          const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
          marker.setPosition(pos);
          reverse(pos);
        });

        // Modern Places Autocomplete (Places API New). Renders its own input.
        try {
          if (maps.places?.PlaceAutocompleteElement && acRef.current) {
            const ac = new maps.places.PlaceAutocompleteElement();
            ac.style.width = "100%";
            acRef.current.innerHTML = "";
            acRef.current.appendChild(ac);
            acElRef.current = ac;
            ac.addEventListener("gmp-select", async (event) => {
              try {
                const prediction = event.placePrediction;
                if (!prediction) return;
                const place = prediction.toPlace();
                await place.fetchFields({ fields: ["formattedAddress", "location"] });
                const loc = place.location;
                if (!loc) return;
                const pos = { lat: loc.lat(), lng: loc.lng() };
                map.panTo(pos);
                map.setZoom(16);
                marker.setPosition(pos);
                emit(pos, place.formattedAddress || null);
              } catch (_) {
                /* ignore selection errors */
              }
            });
          }
        } catch (_) {
          /* Places API (New) unavailable — manual entry still works */
        }

        // For a NEW site (no coordinates yet), auto-pin the user's current GPS location.
        if (!hasVal && "geolocation" in navigator) {
          setLocating(true);
          navigator.geolocation.getCurrentPosition(
            (position) => {
              if (cancelled) return;
              const pos = { lat: position.coords.latitude, lng: position.coords.longitude };
              map.panTo(pos);
              map.setZoom(17);
              marker.setPosition(pos);
              reverse(pos);
              setLocating(false);
            },
            () => {
              if (!cancelled) setLocating(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
          );
        }

        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move the pin when coordinates are typed/pasted into the parent form.
  useEffect(() => {
    const marker = markerRef.current;
    const map = gmapRef.current;
    if (!marker || !map) return;
    const lat = parseFloat(value?.latitude);
    const lng = parseFloat(value?.longitude);
    if (!isFinite(lat) || !isFinite(lng)) return;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
    const le = lastEmitRef.current;
    if (le && Math.abs(le.lat - lat) < 1e-6 && Math.abs(le.lng - lng) < 1e-6) return; // update originated from the map itself
    const pos = { lat, lng };
    marker.setPosition(pos);
    map.panTo(pos);
    if (map.getZoom() < 14) map.setZoom(16);
    lastEmitRef.current = { lat, lng };
  }, [value?.latitude, value?.longitude]);

  if (status === "error") {
    return (
      <div
        className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-3"
        data-testid="map-picker-error"
      >
        <MapPinOff className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <p>
          Map couldn't load. Check that the Google Maps key is valid and that <b>Maps JavaScript API</b>,{" "}
          <b>Places API</b> and <b>Geocoding API</b> are enabled. You can still enter coordinates manually.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="map-picker">
      <div className="relative mb-2" data-testid="map-search-wrap">
        <div ref={acRef} data-testid="map-search-input" className="w-full [&_gmp-place-autocomplete]:w-full" />
      </div>
      <div className={`relative ${height} rounded-xl overflow-hidden border border-slate-200`}>
        <div ref={mapRef} className="absolute inset-0 bg-slate-100" />
        {status === "loading" && (
          <div className="absolute inset-0 grid place-items-center bg-slate-50 z-10">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
          </div>
        )}
        {locating && status === "ready" && (
          <div
            data-testid="map-locating-badge"
            className="absolute top-2 right-2 z-10 flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm ring-1 ring-slate-200"
          >
            <LocateFixed className="h-3.5 w-3.5 animate-pulse text-indigo-600" />
            Finding your location…
          </div>
        )}
      </div>
      <p className="mt-1.5 text-xs text-slate-400">
        We auto-pin your current GPS location for new sites. Search, click the map, or drag the pin to adjust. Use the <b>Map / Satellite</b> toggle for aerial view.
      </p>
    </div>
  );
}
